import { initialGraphState } from '../utils/initialStates';
import { removeDuplicateNodes } from '../utils/dataProcessing';
import { getUniqueTags } from '../utils/tagUtils';
import logo from '../assets/img/HandLogo.png';

function SettingsMenu({
    data,
    setData,
    selectedFile,
    setSelectedFile,
    disableInitialSwitch,
    setDisableInitialSwitch,
    nodeSize,
    setNodeSize,
    isDarkMode,
    setIsDarkMode,
    colorKey,
    setColorKey,
    selectedTags,
    setSelectedTags,
    setShowAllNodes,
}) {
    // Extract color legend if it exists
    const colorLegend = data['color-legend'] && data['color-legend'][colorKey];

    const handleDropdownChange = (event) => {
        const fileName = event.target.value;

        if (fileName === '') {
            setData(initialGraphState); // Reset to the initial graph state
            setSelectedFile('');
        } else {
            const file = require(`../data/${fileName}`);
            const filteredData = removeDuplicateNodes(file);

            setSelectedFile(fileName);
            setData(filteredData);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const jsonData = JSON.parse(e.target.result);
                const filteredData = removeDuplicateNodes(jsonData);
                setData(filteredData);

                // Reset the dropdown to "Select a file" after uploading
                setSelectedFile('');
            };
            reader.readAsText(file);
        }
    };

    // Extract color keys from the first node
    const colorKeys = data.nodes.length > 0 && typeof data.nodes[0].color === 'object'
        ? Object.keys(data.nodes[0].color)
        : ['default'];

    // Extract unique tags from all nodes
    const uniqueTags = getUniqueTags(data.nodes);

    const handleTagChange = (tag) => {
        setSelectedTags(prevTags => {
            if (prevTags.includes(tag)) {
                return prevTags.filter(t => t !== tag);
            } else {
                return [...prevTags, tag];
            }
        });
    };

    function toSentenceCase(str) {
        if (!str) return "";

        return str.toLowerCase().replace(/(?:^|\.\s+)([a-z])/g, function (match, p1) {
            return p1.toUpperCase();
        });
    }

    return (
        <div className="menu"
            style={{
                maxHeight: '90%',
                position: 'absolute',
                top: '0.5rem',
                left: '0.5rem',
                padding: '1rem',
                borderRadius: '1rem',
                textAlign: 'left',
                overflow: 'scroll',
                overflowX: 'hidden',
                margin: '0',
            }}>

            {/* Rainbow Button */}
            <div 
                style={{ 
                    margin: '0', 
                    marginBottom: '1rem', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    textAlign: 'center', 
                    alignSelf: 'center', 
                    alignContent: 'center', 
                    padding: '0' 
                }}
            >
                <button
                    onClick={() => setShowAllNodes(true)} // Show all nodes on click
                    style={{
                        padding: '1rem',
                        width: '100%',
                        background: 'white',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        borderRadius: '0.5rem',
                        display: 'flex',
                        margin: '0',
                        backgroundColor: 'var(--menu-border-color-opposite)',
                        color: 'var(--text-color-opposite)',
                        textAlign: 'center',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                    }}
                >
                    Node Map Visualizations
                    <img src={logo} alt="Hand Logo" style={{ width: '25px', height: '25px', marginLeft: '1rem' }} />
                </button>
            </div>

            {/* Color Legend Section*/}
            <details open>
                <summary style={{ cursor: 'pointer', marginBottom: '0.5rem', fontWeight: 'bold' }}>Color Legend</summary>
                {colorLegend && (
                    <div className="color-legend">
                        <ul style={{ margin: 0, padding: 0, marginBottom: '1rem' }}>
                            {Object.entries(colorLegend).map(([label, rgb]) => (
                                <li key={label} style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', listStyleType: 'none', borderBottom: '1px solid', padding: '0.5rem' }}>
                                    <span>{toSentenceCase(label)}:</span>
                                    <span style={{ backgroundColor: `rgb${rgb}`, marginLeft: '10px', padding: '0 5px', width: '20px', height: '20px', display: 'inline-block' }}></span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </details>

            <details>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Settings</summary>

                {/* Data Section */}
                <details open>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', margin: '0.5rem' }}>Data</summary>
                    <div style={{ margin: '0.5rem 0', padding: '0.5rem 0' }}>
                        <label>Choose a file:<br /></label>
                        <select style={{ width: '100%' }} onChange={handleDropdownChange} value={selectedFile}>
                            <option value="">Select a file</option>
                            <option value="obama_v_others_2007_2011_graph.json">Obama v. Others Sent.t Pre-Election</option>
                            <option value="control_force_graph.json">Control</option>
                            <option value="barack_album_2008_force_graph.json">2008 Obama Album</option>
                        </select>
                    </div>
                    <div style={{ margin: '0.5rem 0', padding: '0.5rem 0' }}>
                        <label>Upload a JSON file:<br /></label>
                        <input type="file" accept=".json" onChange={handleFileChange} />
                    </div>
                </details>

                {/* Color By Section */}
                <details open>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', margin: '0.5rem' }}>Color By</summary>
                    <div style={{ margin: '0.5rem 0', padding: '0.5rem 0', width: '100%' }}>
                        <label>Color by:<br /></label>
                        <select style={{ width: '100%' }} onChange={(e) => setColorKey(e.target.value)} value={colorKey}>
                            {colorKeys.map((key) => (
                                <option key={key} value={key}>{key}</option>
                            ))}
                        </select>
                    </div>
                </details>

                {/* Tag Filter Section */}
                <details open>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', margin: '0.5rem' }}>Filter by Tags</summary>
                    <div style={{ padding: '0.5rem 0' }}>
                        {uniqueTags.map(tag => (
                            <div key={tag}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={selectedTags.includes(tag)}
                                        onChange={() => handleTagChange(tag)}
                                    />
                                    {tag}
                                </label>
                            </div>
                        ))}
                    </div>
                </details>

                {/* Advanced Section */}
                <details>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', margin: '0.5rem' }}>Advanced</summary>
                    <div style={{ padding: '0.5rem 0' }}>
                        <label>Dark Mode:</label>
                        <label className="toggle">
                            <input
                                type="checkbox"
                                checked={isDarkMode}
                                onChange={() => setIsDarkMode(!isDarkMode)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                    <div style={{ padding: '0.5rem 0' }}>
                        <label>Adjust Node Size:<br /></label>
                        <input
                            type="range"
                            min="1"
                            max="200"
                            value={nodeSize}
                            onChange={(e) => setNodeSize(Number(e.target.value))}
                        />
                        <span>{nodeSize}</span>
                    </div>
                    <div style={{ padding: '0.5rem 0' }}>
                        <label>Dis. File Switch:</label>
                        <label className="toggle">
                            <input
                                type="checkbox"
                                checked={disableInitialSwitch}
                                onChange={() => setDisableInitialSwitch(!disableInitialSwitch)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                </details>
            </details>
        </div>
    );
}

export default SettingsMenu;
